import { useEffect, useState, type ReactNode } from 'react';
import { CheckIcon, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api, type TaxRegistrationPage } from './api.js';

/**
 * Helping a merchant register their shop with their tax authority.
 *
 * The one thing every merchant gets wrong is whose company goes on the form:
 * the payment processor's, which is Stripe, never WyoCSPay's. WyoCSPay is
 * software and never touches the money, so it does not belong on a filing that
 * asks who processes the payments.
 *
 * Values that are the merchant's own - their Stripe account id above all - are
 * filled in with their real values and given a copy button, because a
 * hand-typed acct_ id is exactly the field that gets transcribed wrong. The
 * processor's own company details are a starting point, flagged to be confirmed
 * against the merchant's own Stripe invoice, which is the authoritative source
 * and can differ by country.
 *
 * The written guidance is in English, like the rest of the panel. Where it
 * names a field, it quotes the exact Bulgarian label as printed on the form, so
 * the merchant can match it on screen at NRA (НАП). The form illustration is drawn
 * here from the merchant's real values rather than copied from anyone's
 * screenshot.
 */
export function TaxRegistrationView() {
  const [data, setData] = useState<TaxRegistrationPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .taxRegistration()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : 'Could not load this page.'));
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return <Note tone="danger" title="This page could not be loaded"><p style={{ margin: 0 }}>{error}</p></Note>;
  }
  if (!data) return <Spinner label="Loading…" />;

  return data.guide === 'bg' ? <BulgariaGuide data={data} /> : <GenericGuide data={data} />;
}

// ---------------------------------------------------------------------------

/** A value the merchant copies into a form, with a one-press copy button. */
function CopyRow({ label, sublabel, value, hint }: { label: string; sublabel?: string; value: string; hint?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ padding: '11px 0', borderBottom: '1px solid var(--wc-border)' }}>
      <div style={{ fontSize: 12.5, color: 'var(--wc-muted)', marginBottom: 3 }}>
        {label}
        {sublabel ? <span style={{ color: 'var(--wc-faint)' }}>{'  ·  '}{sublabel}</span> : null}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code
          style={{
            fontSize: 14, fontWeight: 600, color: 'var(--wc-ink)',
            background: 'var(--wc-soft)', padding: '4px 8px', borderRadius: 6,
            wordBreak: 'break-all', flex: '1 1 auto', minWidth: 0,
          }}
        >
          {value || '-'}
        </code>
        {value ? (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            style={{ flex: 'none' }}
          >
            {copied ? <><CheckIcon size={13} /> Copied</> : 'Copy'}
          </button>
        ) : null}
      </div>
      {hint ? <div style={{ fontSize: 12, color: 'var(--wc-faint)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div> : null}
    </div>
  );
}

/**
 * An illustration of the form, filled in.
 *
 * Drawn from the merchant's own values rather than copied from a published
 * screenshot: a copyright-safe figure that also shows their real data, which is
 * what stops the transcription error. Numbered callouts key to the explanation
 * beneath it.
 */
function FormMock({ rows }: { rows: Array<{ n: number; field: string; value: string; muted?: boolean }> }) {
  return (
    <div
      style={{
        border: '1px solid var(--wc-border)', borderRadius: 8, overflow: 'hidden',
        background: '#fff', margin: '4px 0 2px',
      }}
    >
      <div
        style={{
          background: 'var(--wc-soft)', padding: '8px 12px', fontSize: 12,
          fontWeight: 700, letterSpacing: 0.4, color: 'var(--wc-ink)',
          borderBottom: '1px solid var(--wc-border)',
        }}
      >
        ПРИЛОЖЕНИЕ 33 · РАЗДЕЛ 7.2.2 · ВИРТУАЛЕН ПОС ЧРЕЗ ПЛАТЕЖЕН ПРОЦЕСОР
      </div>
      {rows.map((r) => (
        <div
          key={r.n}
          style={{
            display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--wc-border)',
          }}
        >
          <div
            style={{
              flex: '0 0 46%', padding: '9px 12px', fontSize: 12.5, color: 'var(--wc-muted)',
              borderRight: '1px solid var(--wc-border)', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span
              style={{
                flex: 'none', width: 18, height: 18, borderRadius: 9, background: 'var(--wc-accent, #3d6285)',
                color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              {r.n}
            </span>
            {r.field}
          </div>
          <div
            style={{
              flex: '1 1 auto', padding: '9px 12px', fontSize: 13.5, minWidth: 0, wordBreak: 'break-all',
              fontFamily: r.muted ? 'inherit' : 'var(--wc-mono, ui-monospace, Menlo, monospace)',
              fontWeight: r.muted ? 400 : 600,
              color: r.muted ? 'var(--wc-faint)' : 'var(--wc-ink)',
              fontStyle: r.muted ? 'italic' : 'normal',
            }}
          >
            {r.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function BulgariaGuide({ data }: { data: TaxRegistrationPage }) {
  const p = data.processor;
  const acct = data.merchant.stripeAccountId ?? '';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="What goes on the form">
        <p style={{ margin: 0 }}>
          When you register your online shop with the Bulgarian NRA (НАП) under Наредба Н-18 (Приложение 33), the
          payment section asks for the payment provider. Everything you need is filled in below. The
          provider is Stripe, and your identifier is filled in too, so you can copy each value straight
          onto the form.
        </p>
      </Note>

      <Panel title="Раздел 7.1 · Which payment method to tick">
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          Tick <b>„Плащане с карта чрез виртуален ПОС"</b> (card payment through a virtual POS). If you also
          accept cash on delivery, tick that too, but the virtual POS is the part Stripe provides.
        </p>
      </Panel>

      <Panel title="Раздел 7.2.2 · Fill this, filled in from your account">
        <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
          Complete <b>this</b> section, not 7.2.1. Section 7.2.1 is only for a virtual POS a bank issued
          against your own bank account; with Stripe that is not the case. The illustration below shows the
          section with your values in place. The numbers key to the notes underneath.
        </p>

        <FormMock
          rows={[
            { n: 1, field: 'Наименование', value: p.name },
            { n: 2, field: 'Държава', value: p.country },
            { n: 3, field: 'Данъчен номер', value: p.vat },
            { n: 4, field: '№ на виртуален POS', value: acct || '(your Stripe account)' },
            { n: 5, field: 'Уникален идентификатор на търговеца', value: acct || '(your Stripe account)' },
            { n: 6, field: 'Номер на платежна сметка', value: 'your business IBAN', muted: true },
            { n: 7, field: 'Титуляр на сметката', value: data.merchant.name },
          ]}
        />

        <ol style={{ margin: '12px 0 4px', paddingLeft: 20, fontSize: 13, color: 'var(--wc-body)', lineHeight: 1.6 }}>
          <li><b>Наименование</b> - the processor's legal name.</li>
          <li><b>Държава</b> - the processor's country.</li>
          <li><b>Данъчен номер</b> - the processor's VAT number.</li>
          <li><b>№ на виртуален POS</b> - there is no bank-style POS terminal number here, so this field takes your merchant identifier, the same value as the next field.</li>
          <li><b>Уникален идентификатор на търговеца</b> - your merchant identifier, starting <code>acct_</code>. The one field worth copying rather than retyping.</li>
          <li><b>Номер на платежна сметка</b> - your business IBAN, the account your money is paid out to.</li>
          <li><b>Титуляр на сметката</b> - the account holder, i.e. your business.</li>
        </ol>

        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--wc-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 2px' }}>
            Copy these straight across
          </div>
          <CopyRow label="Наименование" sublabel="processor name" value={p.name} />
          <CopyRow label="Държава" sublabel="processor country" value={p.country} />
          <CopyRow label="Данъчен номер" sublabel="processor VAT" value={p.vat} />
          {p.regNumber ? <CopyRow label="Company registration number" sublabel="if the form asks" value={p.regNumber} /> : null}
          <CopyRow label="Уникален идентификатор на търговеца" sublabel="merchant identifier" value={acct}
            hint="Starts with acct_. The one field worth copying rather than retyping." />
          <CopyRow label="Титуляр на сметката" sublabel="account holder" value={data.merchant.name} />
        </div>
      </Panel>

      <Panel title="Раздел 4.2 · Your shop's own details">
        <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6 }}>
          These fields are about <b>your own website</b>, not WyoCSPay: the shop's domain, who owns it, the
          hosting, the software you run. You fill them in for the store you sell from. If NRA (НАП) asks where the
          payment itself happens, the payment page is below; the shop domain, though, is your own site and
          that is what goes in 4.2 as the store address.
        </p>
        <CopyRow label="Payment page" sublabel="where the customer pays" value={data.merchant.payUrl} />
      </Panel>

      <Note tone="warn" title="Check before you file">
        <p style={{ margin: 0 }}>
          This page saves you the retyping; it does not replace your accountant. Which fields your NRA (НАП)
          submission needs is worth a word with an accountant, and the official rules are in{' '}
          <a href="https://nra.bg" target="_blank" rel="noreferrer">Наредба Н-18 на НАП</a>.
        </p>
      </Note>
    </div>
  );
}

function GenericGuide({ data }: { data: TaxRegistrationPage }) {
  const p = data.processor;
  const acct = data.merchant.stripeAccountId ?? '';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Who to declare as your payment processor">
        <p style={{ margin: 0 }}>
          Many countries require an online shop to register which payment mechanism it uses. Whatever the
          form is called where you are, the payment processor to declare is <b>Stripe</b>, not WyoCSPay.
          WyoCSPay is the software you take payments through; the money is processed by Stripe and settles
          directly to your Stripe account. Your unique identifier with the processor is your Stripe account
          id below.
        </p>
      </Note>

      <Panel title="Your details for the form">
        <FormMock
          rows={[
            { n: 1, field: 'Payment processor', value: p.name },
            { n: 2, field: 'Processor country', value: p.country },
            ...(p.vat ? [{ n: 3, field: 'Processor VAT', value: p.vat }] : []),
            { n: p.vat ? 4 : 3, field: 'Your merchant identifier', value: acct || '(your Stripe account)' },
            { n: p.vat ? 5 : 4, field: 'Account holder', value: data.merchant.name },
          ]}
        />
        <div style={{ marginTop: 12 }}>
          <CopyRow label="Payment processor" value={p.name}
            hint={p.verifyOnInvoice ? "Stripe's standard entity for your region. Confirm the exact name on your own Stripe invoice; it can differ by country." : undefined} />
          <CopyRow label="Processor country" value={p.country} />
          {p.vat ? <CopyRow label="Processor tax / VAT number" value={p.vat}
            hint="Confirm against your own Stripe invoice before filing." /> : null}
          {p.regNumber ? <CopyRow label="Company registration number" value={p.regNumber} /> : null}
          <CopyRow label="Your unique merchant identifier" value={acct}
            hint="Your Stripe account, starting acct_. Copy it rather than retype it." />
          <CopyRow label="Account holder / merchant" value={data.merchant.name} />
        </div>
      </Panel>

      <Note tone="warn" title="Confirm before you file">
        <p style={{ margin: 0 }}>
          This page saves you the retyping; it does not replace your accountant. Which form applies in
          {' '}{data.merchant.country || 'your country'}, and exactly which fields it needs, is for a local
          advisor to confirm. The exact Stripe entity and tax number are printed on your Stripe invoice.
        </p>
      </Note>
    </div>
  );
}
